import adminAlertTemplateApi, { resolveAssetUrl, toStoragePath } from './adminAlertTemplate.api';

const TEMPLATE_DEFAULTS = {
  layout: '{}',
  actions: '{}',
  buttonlayout: '[]',
  edittextdata: '[]',
  generatedlayout: '[]',
  dimensions: '{"width":720,"height":1000}',
  catlist: [],
  delay: '0',
  previewurl: '',
  imageurl: '',
  industrytypeid: -1,
  availableglobal: true,
  isemergency: false,
  description: '',
  type: 'alert',
  status: 'template',
};

function applyDefaults(data) {
  const fixed = { ...data };
  Object.keys(TEMPLATE_DEFAULTS).forEach((key) => {
    if (fixed[key] === undefined || fixed[key] === null) {
      fixed[key] = TEMPLATE_DEFAULTS[key];
    }
  });
  return fixed;
}

function hydrateForDisplay(data) {
  const fixed = applyDefaults(data);
  if (fixed.previewurl) fixed.previewurl = resolveAssetUrl(fixed.previewurl);
  if (fixed.imageurl) fixed.imageurl = resolveAssetUrl(fixed.imageurl);
  if (fixed.applayout?.previewImage) {
    fixed.applayout = {
      ...fixed.applayout,
      previewImage: resolveAssetUrl(fixed.applayout.previewImage),
    };
  }
  if (Array.isArray(fixed.applayout?.imageLayout)) {
    fixed.applayout = {
      ...fixed.applayout,
      imageLayout: fixed.applayout.imageLayout.map((entry) => (
        entry?.url
          ? { ...entry, url: resolveAssetUrl(entry.url) }
          : entry
      )),
    };
  }
  return fixed;
}

function normalizeLayoutForStorage(layout) {
  if (layout == null || layout === '') return layout;

  let parsed = layout;
  if (typeof layout === 'string') {
    try {
      parsed = JSON.parse(layout);
    } catch {
      return layout;
    }
  }

  if (!parsed?.objects || !Array.isArray(parsed.objects)) {
    return layout;
  }

  const walk = (objects) => {
    objects.forEach((obj) => {
      if (!obj || typeof obj !== 'object') return;
      ['src', 'uploadUrl', 'url'].forEach((key) => {
        if (typeof obj[key] === 'string' && obj[key].includes('/uploads/')) {
          obj[key] = toStoragePath(obj[key]);
        }
      });
      if (Array.isArray(obj.objects)) walk(obj.objects);
    });
  };

  const copy = JSON.parse(JSON.stringify(parsed));
  walk(copy.objects);
  return typeof layout === 'string' ? JSON.stringify(copy) : copy;
}

function toStoragePayload(data) {
  const fixed = applyDefaults(data);
  if (fixed.previewurl) fixed.previewurl = toStoragePath(fixed.previewurl);
  if (fixed.imageurl) fixed.imageurl = toStoragePath(fixed.imageurl);
  if (fixed.applayout) {
    const applayout = { ...fixed.applayout };
    if (applayout.previewImage) {
      applayout.previewImage = toStoragePath(applayout.previewImage);
    }
    if (Array.isArray(applayout.imageLayout)) {
      applayout.imageLayout = applayout.imageLayout.map((entry) => (
        entry?.url
          ? { ...entry, url: toStoragePath(entry.url) }
          : entry
      ));
    }
    fixed.applayout = applayout;
  }
  if (fixed.layout != null) {
    fixed.layout = normalizeLayoutForStorage(fixed.layout);
  }
  return fixed;
}

function unwrap(res) {
  if (!res?.success) {
    throw new Error(res?.message || 'Request failed');
  }
  return res.data;
}

export const templateApi = {
  mode: 'api',

  async loadById(id) {
    const res = await adminAlertTemplateApi.getById(id);
    const t = unwrap(res);
    return t ? hydrateForDisplay(t) : null;
  },

  async save(template) {
    const payload = toStoragePayload({ ...template });
    let res;
    if (payload.id) {
      res = await adminAlertTemplateApi.update(payload.id, payload);
    } else {
      res = await adminAlertTemplateApi.create(payload);
    }
    return hydrateForDisplay(unwrap(res));
  },

  async deleteTemplate(id) {
    await adminAlertTemplateApi.delete(id);
  },

  async listAll(type) {
    const params = {};
    if (type) params.type = type;
    const res = await adminAlertTemplateApi.list(params);
    return unwrap(res).map(hydrateForDisplay);
  },

  async listByStatus(status, type = 'alert') {
    const res = await adminAlertTemplateApi.list({ status, type });
    return unwrap(res).map(hydrateForDisplay);
  },

  async listPickerOptions({ search, type, exclude, highlight, limit } = {}) {
    const params = { limit };
    if (search) params.q = search;
    if (type) params.type = type;
    if (exclude) params.exclude = exclude;
    if (highlight) params.highlight = highlight;
    const res = await adminAlertTemplateApi.listPickerOptions(params);
    return unwrap(res);
  },

  async createNew(name, status = 'template', type = 'alert') {
    const res = await adminAlertTemplateApi.create({
      name,
      status,
      type,
      layout: '{"objects":[],"background":"#FFFFFF"}',
      dimensions: '{"width":720,"height":1000}',
      applayout: {
        backgroundColor: '255,255,255,1',
        previewImage: '',
        buttonLayout: [],
        textLayout: [],
        imageLayout: [],
        editableLayout: [],
      },
    });
    return hydrateForDisplay(unwrap(res));
  },

  async clone(source, status = 'template', type = 'alert') {
    const res = await adminAlertTemplateApi.clone(source.id);
    const cloned = hydrateForDisplay(unwrap(res));
    if (status !== 'template' || type !== 'alert') {
      return templateApi.save({ ...cloned, status, type });
    }
    return cloned;
  },

  async publish(templateId) {
    const res = await adminAlertTemplateApi.publish(templateId);
    return hydrateForDisplay(unwrap(res));
  },

  createEmpty(type = 'alert', status = 'template') {
    return {
      ...TEMPLATE_DEFAULTS,
      name: 'Create',
      type,
      status,
      applayout: {
        backgroundColor: '255,255,255,1',
        previewImage: '',
        buttonLayout: [],
        textLayout: [],
        imageLayout: [],
        editableLayout: [],
      },
    };
  },
};

export default templateApi;
